/*
 * DSD Surface Registry - p11dz142
 * Governance-only surface registry for canonical social surfaces.
 * Defines where canonical social objects may render without creating new renderers,
 * Firebase reads, Firebase writes, or realtime listeners.
 */
(function(){
  'use strict';
  if (window.DSDSurfaceRegistry && window.DSDSurfaceRegistry.version) return;

  var VERSION = 'p11dz142-surface-registry-v1';
  var OWNER = 'SurfaceRegistry';
  var FIREBASE_GATE = 'disabled-surface-registry-governance-only';

  function claim(surface, meta){
    try {
      if (window.DSDRegistry && typeof window.DSDRegistry.claim === 'function') {
        window.DSDRegistry.claim(surface, OWNER, Object.assign({ version: VERSION }, meta || {}));
      }
    } catch(e) {}
  }

  function exists(name){ return !!window[name]; }

  var surfaceTypes = {
    globalFeed: {
      key: 'globalFeed',
      label: 'Members / Community Feed',
      routeScope: 'members-community-feed',
      anchorSelectors: ['#dsd-feed-container'],
      controllerOwner: 'LaunchSocialActivation',
      feedOwner: 'LaunchSocialActivation',
      rendererOwner: 'LaunchSocialActivation',
      postRenderer: 'LaunchSocialActivation',
      allowedObjects: ['post'],
      filterContract: 'public-or-member-visible canonical posts',
      mayOwnRenderers: false,
      mayOwnPersistence: false
    },
    profileFeed: {
      key: 'profileFeed',
      label: 'Member Profile Feed',
      routeScope: 'user-profile',
      anchorSelectors: ['#dsd-profile-container', '.dsd-profile-feed'],
      controllerOwner: 'LaunchSocialActivation/ProfilePostScope',
      feedOwner: 'LaunchSocialActivation',
      rendererOwner: 'ProfileRenderer delegates to LaunchSocialActivation',
      postRenderer: 'LaunchSocialActivation',
      allowedObjects: ['post'],
      filterContract: 'author/profile scoped canonical posts only',
      mayOwnRenderers: false,
      mayOwnPersistence: false
    },
    discoverySurface: {
      key: 'discoverySurface',
      label: 'Member Discovery Surface',
      routeScope: 'members-community-feed',
      anchorSelectors: ['[data-dsd-contract="member-discovery"]', '[data-dsd-contract="member-discovery-grid"]'],
      controllerOwner: 'MemberSearchController',
      rendererOwner: 'DiscoveryRenderer/ProfileCardRenderer',
      allowedObjects: ['profile'],
      filterContract: 'profile discovery/search only; does not render posts directly',
      mayOwnRenderers: false,
      mayOwnPersistence: false
    },
    notificationSurface: {
      key: 'notificationSurface',
      label: 'Notification Surface',
      routeScope: 'global-social-shell',
      anchorSelectors: ['#dsd-notification-root', '.dsd-notification-button'],
      controllerOwner: 'FutureNotificationStore/NotificationRegistry',
      rendererOwner: 'NotificationRenderer',
      allowedObjects: ['notification'],
      filterContract: 'recipient scoped notifications only',
      mayOwnRenderers: false,
      mayOwnPersistence: false
    },
    modalSurface: {
      key: 'modalSurface',
      label: 'Immersive Overlay Surface',
      routeScope: 'global-overlay',
      anchorSelectors: ['[data-dsd-immersive-overlay-root]', '[data-dsd-immersive-overlay]'],
      controllerOwner: 'ImmersiveOverlayController',
      rendererOwner: 'ImmersiveOverlayController',
      allowedObjects: ['story'],
      filterContract: 'immersive overlay displays authorized story payloads only; post media renders inline and does not open a modal',
      mayOwnRenderers: true,
      mayOwnPersistence: false
    },
    futureStoryRail: {
      key: 'futureStoryRail',
      label: 'Future Story Rail',
      routeScope: 'global-story-rail',
      anchorSelectors: ['future:[data-dsd-surface="stories"]'],
      controllerOwner: 'FutureStoryStore/StoryController',
      rendererOwner: 'StoriesRenderer',
      allowedObjects: ['story'],
      filterContract: 'story visibility + expiration scoped canonical story objects',
      mayOwnRenderers: false,
      mayOwnPersistence: false,
      activationGate: 'Requires Story Ownership Manifest before implementation.'
    },
    futureUniverseSurface: {
      key: 'futureUniverseSurface',
      label: 'Future Universe Surface',
      routeScope: 'future-universe',
      anchorSelectors: ['future:[data-dsd-surface="universe"]'],
      controllerOwner: 'FutureUniverseController',
      rendererOwner: 'LaunchSocialActivation via canonical surface scope',
      allowedObjects: ['post', 'story', 'galaxy-card', 'event'],
      filterContract: 'universe scoped canonical objects only',
      mayOwnRenderers: false,
      mayOwnPersistence: false,
      activationGate: 'Universe UI may only filter canonical objects; no universe-specific post renderer.'
    },
    futureGalaxySurface: {
      key: 'futureGalaxySurface',
      label: 'Future Galaxy Surface',
      routeScope: 'future-galaxy',
      anchorSelectors: ['future:[data-dsd-surface="galaxy"]'],
      controllerOwner: 'FutureGalaxyController',
      rendererOwner: 'LaunchSocialActivation via canonical surface scope',
      allowedObjects: ['post', 'story', 'event', 'chat-preview'],
      filterContract: 'galaxy membership/visibility scoped canonical objects only',
      mayOwnRenderers: false,
      mayOwnPersistence: false,
      activationGate: 'Galaxy UI may own membership/governance identity, not canonical post rendering.'
    },
    futureGalaxyChatSurface: {
      key: 'futureGalaxyChatSurface',
      label: 'Future Galaxy Chat Surface',
      routeScope: 'future-galaxy-chat',
      anchorSelectors: ['future:[data-dsd-surface="galaxy-chat"]'],
      controllerOwner: 'FutureGalaxyChatStore/FutureGalaxyChatController',
      rendererOwner: 'MessagingRenderer',
      allowedObjects: ['chat-message'],
      filterContract: 'galaxy scoped conversation messages with moderation/rate limits',
      mayOwnRenderers: false,
      mayOwnPersistence: false,
      activationGate: 'Requires Messaging Ownership Manifest, moderation hooks, and realtime cleanup contract.'
    }
  };

  var surfaceDoctrine = [
    'A surface is a scoped view of canonical social objects, not a separate data architecture.',
    'Surfaces may filter, sort, and present canonical objects.',
    'Surfaces may not create duplicate renderers, duplicate post stores, or direct Firebase ownership.',
    'Feed-like surfaces must use LaunchSocialActivation for social rendering unless a future Architecture Gate explicitly approves otherwise.',
    'Profile, universe, and galaxy feeds are filtered views of canonical posts.',
    'Immersive overlay surfaces display authorized story detail; post media renders inline and does not open a modal.',
    'Future Multiverse/Universe/Galaxy/Story/Chat surfaces must register here before implementation.'
  ];

  var objectToSurfaceRules = {
    post: ['globalFeed', 'profileFeed', 'futureUniverseSurface', 'futureGalaxySurface', 'modalSurface'],
    profile: ['discoverySurface', 'profileFeed', 'modalSurface'],
    notification: ['notificationSurface'],
    story: ['futureStoryRail', 'futureUniverseSurface', 'futureGalaxySurface'],
    event: ['futureUniverseSurface', 'futureGalaxySurface'],
    galaxy: ['futureUniverseSurface'],
    chatMessage: ['futureGalaxyChatSurface']
  };

  function currentSurfacePresence(){
    return Object.keys(surfaceTypes).reduce(function(acc, key){
      var surface = surfaceTypes[key];
      acc[key] = {
        label: surface.label,
        routeScope: surface.routeScope,
        anchors: surface.anchorSelectors.map(function(selector){
          if (selector.indexOf('future:') === 0) return { selector: selector, count: 0, future: true };
          try { return { selector: selector, count: document.querySelectorAll(selector).length, future: false }; }
          catch(e) { return { selector: selector, count: 0, error: true }; }
        }),
        ownerPresent: exists(surface.rendererOwner) || false,
        mayOwnRenderers: !!surface.mayOwnRenderers,
        mayOwnPersistence: !!surface.mayOwnPersistence
      };
      return acc;
    }, {});
  }

  function dependencyReadiness(){
    return {
      DSDRegistry: exists('DSDRegistry'),
      LaunchSocialActivation: exists('DSDLaunchSocial'),
      GMOR: exists('DSDGMOR'),
      ProfilePostScope: exists('DSDProfilePostScope'),
      FeedRenderer: exists('DSDLaunchSocial'),
      PostRenderer: exists('DSDLaunchSocial'),
      ProfileRenderer: exists('DSDProfileRenderer'),
      ImmersiveOverlayController: exists('DSDImmersiveOverlayController'),
      NotificationDependencyGraph: exists('DSDNotificationDependencyGraph'),
      FirebaseSocialSchema: exists('DSDFirebaseSocialSchema'),
      RealtimeOwnershipAudit: exists('DSDRealtimeOwnershipAudit'),
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false
    };
  }

  function getSurface(key){ return surfaceTypes[key] || null; }
  function listSurfaces(){ return Object.keys(surfaceTypes).map(function(k){ return surfaceTypes[k]; }); }
  function getEligibleSurfacesForObject(objectType){ return objectToSurfaceRules[objectType] || []; }
  function validateRenderRequest(objectType, surfaceKey){
    var eligible = getEligibleSurfacesForObject(objectType);
    var surface = getSurface(surfaceKey);
    return {
      valid: !!surface && eligible.indexOf(surfaceKey) !== -1,
      objectType: objectType,
      surfaceKey: surfaceKey,
      eligibleSurfaces: eligible,
      surface: surface,
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false
    };
  }

  function getReport(reason){
    return {
      version: VERSION,
      reason: reason || 'manual-report',
      firebaseGate: FIREBASE_GATE,
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      doctrine: surfaceDoctrine,
      surfaces: surfaceTypes,
      objectToSurfaceRules: objectToSurfaceRules,
      currentSurfacePresence: currentSurfacePresence(),
      dependencyReadiness: dependencyReadiness(),
      nextGate: 'p11dz143-visibility-layer-or-controlled-read-only-post-activation-gate'
    };
  }

  function publish(reason){
    var report = getReport(reason || 'publish');
    claim('surface-registry:doctrine', { doctrine: surfaceDoctrine, count: surfaceDoctrine.length });
    claim('surface-registry:surface-map', { surfaces: listSurfaces(), count: listSurfaces().length });
    claim('surface-registry:object-routing', objectToSurfaceRules);
    claim('surface-registry:current-presence', report.currentSurfacePresence);
    claim('surface-registry:dependency-readiness', report.dependencyReadiness);
    claim('surface-registry:firebase-firewall', {
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      gate: FIREBASE_GATE
    });
    try { window.dispatchEvent(new CustomEvent('dsd:surface-registry-ready', { detail: report })); } catch(e) {}
    window.DSDSurfaceRegistryReport = report;
    return report;
  }

  window.DSDSurfaceRegistry = {
    version: VERSION,
    owner: OWNER,
    getReport: getReport,
    publish: publish,
    getSurface: getSurface,
    listSurfaces: listSurfaces,
    getEligibleSurfacesForObject: getEligibleSurfacesForObject,
    validateRenderRequest: validateRenderRequest,
    currentSurfacePresence: currentSurfacePresence
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ publish('dom-ready'); }, { once: true });
  } else {
    publish('already-ready');
  }
})();
